import { onBeforeUnmount, ref, watch, type Ref } from 'vue'
import type { AdventureSkin, SceneSkin } from './pip-skins'
import type { StreamHudData } from './stream-hud'
import { ExpRateMotionTracker } from './stream-motion'

type Feedback = 'awake' | 'rest' | 'farewell' | 'rise' | 'fall' | 'level-up'

/** Decorative life is independent of recording; explicit pause/end states stay still. */
export function isIdleMotionActive(status: string): boolean {
  return status === 'idle' || status === 'starting' || status === 'running'
}

export interface AdventureMotionOptions {
  actorSelector?: string
  rotateAccent?: boolean
  particles?: 'burst' | 'bubbles'
  effects?: Partial<Record<Feedback, { selector: string; frames: Keyframe[] }[]>>
}

/** Local presentation effects only; the existing snapshot remains the source of truth. */
export function useAdventureMotion(
  root: Ref<HTMLElement | undefined>,
  input: () => { data: StreamHudData; skin: AdventureSkin | SceneSkin | 'stream-mushroom'; preview?: boolean },
  options: AdventureMotionOptions = {},
) {
  const feedback = ref<Feedback>()
  const rate = new ExpRateMotionTracker()
  let priority = 0
  let animations: Animation[] = []
  let timer: ReturnType<typeof setTimeout> | undefined
  function clear() {
    clearTimeout(timer)
    animations.forEach(animation => animation.cancel())
    animations = []
    feedback.value = undefined
    priority = 0
  }
  function play(event: Feedback, importance: number) {
    const node = root.value
    if (!node || importance < priority) return
    clear()
    feedback.value = event
    priority = importance
    const animate = (target: Element | null, frames: Keyframe[]) => {
      const animation = target?.animate?.(frames, { duration: 560, easing: 'ease-out' })
      if (animation) animations.push(animation)
    }
    const transforms: Record<Feedback, string> = {
      awake: 'translateY(-.25rem) rotate(-5deg)', rest: 'translateY(.125rem) scaleY(.92)',
      farewell: 'rotate(12deg) scale(.94)', rise: 'translateY(-.375rem) rotate(5deg)',
      fall: 'translateY(.1875rem) rotate(-7deg)', 'level-up': 'translateY(-.75rem) scale(1.08)',
    }
    animate(node.querySelector(options.actorSelector ?? '.adventure-companion'), [
      { transform: 'none' }, { transform: transforms[event], offset: .45 }, { transform: 'none' },
    ])
    if (options.rotateAccent !== false && (event === 'rise' || event === 'level-up')) {
      animate(node.querySelector('[data-feedback-accent]'), [{ transform: 'rotate(0)' }, { transform: 'rotate(360deg)' }])
    }
    options.effects?.[event]?.forEach(({ selector, frames }) => {
      node.querySelectorAll(selector).forEach(target => animate(target, frames))
    })
    if (event === 'level-up') {
      // Particles stay opaque: no soft fades into the chroma-key canvas.
      const particles = node.querySelectorAll('[data-feedback-particle]')
      particles.forEach((particle, index) => {
        const x = (index - (particles.length - 1) / 2) * .65
        animate(particle, options.particles === 'bubbles' ? [
          { transform: `translate(${x}rem, .375rem) scale(.5)` },
          { transform: `translate(${x}rem, -.625rem) scale(1)`, offset: .55 },
          { transform: `translate(${x * 1.1}rem, -1.5rem) scale(.5)` },
        ] : [
          { transform: 'translate(0, 0) rotate(0)' },
          { transform: `translate(${x}rem, -1.5rem) rotate(${index * 35}deg)`, offset: .55 },
          { transform: `translate(${x * 1.35}rem, -.375rem) rotate(${index * 65}deg)` },
        ])
      })
    }
    timer = setTimeout(clear, 580)
  }
  watch(input, (current, previous) => {
    const { data, skin, preview } = current
    const direction = rate.observe(data.expRateValue, Date.now(), `${data.sessionId}:${skin}`, data.status === 'running')
    if (!previous) return
    if (previous.skin !== skin) { clear(); return }
    const sameSession = previous.data.sessionId === data.sessionId
    if (!sameSession) clear()
    if (data.status !== previous.data.status) clear()
    if (preview) return
    if (sameSession && data.status === 'running' && data.levelUpId && data.levelUpId !== previous.data.levelUpId) {
      play('level-up', 3)
    } else if (data.status !== previous.data.status) {
      if (data.status === 'running') play('awake', 2)
      else if (data.status === 'paused') play('rest', 2)
      else if (data.status === 'ended') play('farewell', 2)
    } else if (sameSession && direction) play(direction, 1)
  }, { immediate: true, flush: 'post' })
  onBeforeUnmount(clear)
  return feedback
}
